import { App } from 'App';
import { ChangeEventHandler, FocusEventHandler, useMemo } from 'react';
import { FieldGroup, Heading, Select, Spacer } from 'internalLibrary';
import { QuestionnaireFormQuestion } from 'types/ui';
import { useAnalytics } from 'use-analytics';
import { useTranslation } from 'react-i18next';
import layout from '../../../../scss/layout.module.scss';
import RadioButtonGroup from 'ui/components/RadioButtonGroup';

export type QuestionnaireQuestionProps = {
  question: QuestionnaireFormQuestion;
  value: string;
  onChange: ChangeEventHandler<HTMLSelectElement | HTMLInputElement>;
  onBlur: FocusEventHandler<HTMLSelectElement | HTMLInputElement>;
  touched: boolean;
  error?: string;
};

export const QuestionnaireQuestion = (props: QuestionnaireQuestionProps) => {
  const { question, value, onChange, onBlur, error } = props;
  const { track } = useAnalytics();
  const { t } = useTranslation();
  const otherOptions = useMemo(() => {
    if (question.questionType === 'SINGLE_SELECT_OTHER') {
      return [
        {
          name: App.translate('questionnaire.other'),
          value: question.id + '_-1',
        },
      ];
    }
    return [];
  }, [question.id, question.questionType]);

  const valueChanged: ChangeEventHandler<
    HTMLSelectElement | HTMLInputElement
  > = (e) => {
    track('input', {
      description: question.questionReference,
      inputPlaceholder: question.questionText,
      value: e.target.value,
    });
    onChange(e);
  };

  if (['PEP', 'BEHALF'].includes(question.questionReference)) {
    const options = question.answers.map((answer) => ({
      name: t(answer.answerText as any),
      value: question.id + '_' + answer.id,
    }));

    return (
      <Spacer marginTop={{ default: '45px', s: '30px' }}>
        <Heading level="h5" text={question.questionText} weight={'medium'} />
        <Spacer
          marginBottom={{ default: '30px', s: '19px' }}
          className={layout['width-md']}
        >
          <FieldGroup
            validationText={error}
            helperText={
              question.questionReference === 'BEHALF'
                ? t('questionnaire_behalf_helperText')
                : undefined
            }
          >
            <RadioButtonGroup
              name={question.questionReference}
              onChange={valueChanged}
              onBlur={onBlur}
              options={options}
              value={value}
            />
          </FieldGroup>
        </Spacer>
      </Spacer>
    );
  } else {
    const options = [
      {
        name: '...',
        value: '',
      },
      ...question.answers.map((answer) => ({
        name: answer.answerText,
        value: question.id + '_' + answer.id,
      })),
      ...otherOptions,
    ];

    return (
      <Spacer marginTop={{ default: '45px', s: '30px' }}>
        <Heading level="h5" text={question.questionText} weight={'medium'} />
        <Spacer
          marginBottom={{ default: '30px', s: '19px' }}
          className={layout['width-md']}
        >
          <FieldGroup validationText={error}>
            <Select
              label={t('questionnaire_selectAnswer')}
              name={question.questionReference}
              onChange={valueChanged}
              onBlur={onBlur}
              value={value}
              options={options}
            />
          </FieldGroup>
        </Spacer>
      </Spacer>
    );
  }
};
